import React from 'react'
import { getRequiredSystems, SystemTypes } from '../forms/is-required-system'

interface ObjectiveExpandedRowProps {
    objective: any
    onEditClick: (objective: any) => void
}

const IconPencil = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
)

const DetailItem: React.FC<{ label: string; value: any; format?: (v: any) => string }> = ({ label, value, format }) => (
    <div className="objective-detail-item">
        <span className="detail-label">{label}</span>
        <span className="detail-value">{format ? format(value) : (value ?? '—')}</span>
    </div>
)

const DetailText: React.FC<{ label: string; value: any }> = ({ label, value }) => (
    <div className="objective-detail-text">
        <span className="detail-label">{label}</span>
        <p className="detail-value">{value || '—'}</p>
    </div>
)

const SYSTEM_LABEL: Record<string, string> = {
    [SystemTypes.IN_SCOPE]: 'Main System (In Scope)',
    [SystemTypes.HR]: 'HR System',
    [SystemTypes.TICKET]: 'Ticket System',
}

export const ObjectiveExpandedRow: React.FC<ObjectiveExpandedRowProps> = ({ objective, onEditClick }) => {
    const requiredSystems = getRequiredSystems(objective.objectiveNumber ?? null)

    return (
        <div className="objective-expanded-row">
            <div className="objective-expanded-header">
                <h3 className="objective-expanded-name">{objective.name}</h3>
                <button className="edit-btn" onClick={() => onEditClick(objective)}>
                    <IconPencil /> Edit
                </button>
            </div>

            <div className="expanded-sections">
                <div className="detail-section-card">
                    <div className="detail-section-label">General</div>
                    <div className="objective-detail-grid">
                        <DetailItem label="Objective Number" value={objective.objectiveNumber} />
                        <DetailItem label="Abbreviation" value={objective.abbreviation} />
                        <DetailItem label="Key Objective" value={objective.isKeyObjective} format={(v: any) => v ? 'Yes' : 'No'} />
                    </div>
                </div>

                <div className="detail-section-card">
                    <div className="detail-section-label">Required Systems</div>
                    <div className="required-systems-badges">
                        {requiredSystems.map(s => (
                            <span key={s} className="system-badge">{SYSTEM_LABEL[s] || s}</span>
                        ))}
                    </div>
                </div>

                <div className="detail-section-card">
                    <div className="detail-section-label">Domain</div>
                    <div className="objective-detail-grid">
                        <DetailItem label="Domain" value={objective.domain} />
                        <DetailItem label="Domain Abbreviation" value={objective.domainAbbreviation} />
                    </div>
                </div>

                <div className="detail-section-card">
                    <div className="detail-section-label">Classification</div>
                    <div className="objective-detail-grid">
                        <DetailItem label="Frequency" value={objective.frequency} />
                        <DetailItem label="Type" value={objective.type} />
                        <DetailItem label="IT Dependency" value={objective.ITDependency} />
                        <DetailItem label="Test Type" value={objective.testType} />
                        <DetailItem label="Specification" value={objective.specification} />
                        <DetailItem label="Population Type" value={objective.populationType} />
                    </div>
                </div>

                <div className="detail-section-card">
                    <div className="detail-section-label">Description</div>
                    <DetailText label="Description" value={objective.description} />
                    <DetailText label="Risk" value={objective.risk} />
                </div>

                <div className="detail-section-card">
                    <div className="detail-section-label">Criteria</div>
                    <DetailText label="Failure Criteria" value={objective.failureCriteria} />
                    <DetailText label="Test Plan" value={objective.testPlan} />
                </div>
            </div>
        </div>
    )
}
